/**
 * useApplicationTitles — load a competition year's ACTIVE, in-scope season
 * Title_Catalog for contestant/director title selection
 * (season-scoped-title-registry spec, Req 5.1, 5.4).
 *
 * The catalog is the source of title *definitions*; the contestant apply UI and
 * the director review approved-title control present these director-defined
 * titles as selectable options instead of free text. This hook reads the
 * catalog through the `applicationFormClient.listTitles(competitionYear)`
 * transport seam — the SAME seam the surrounding apply/review surfaces already
 * use — so a surface flips mock↔live with no call-site change.
 *
 * NEW-SEASON INDEPENDENCE (Req 5.4): the catalog is resolved STRICTLY by the
 * supplied `competitionYear`. Nothing here reads or carries over a prior
 * season's titles or preferences — a new season presents only its own catalog.
 *
 * Selectable-set filtering mirrors `useApplicationDivisions`:
 *   - keep only `active` titles (inactive entries are retained in the catalog
 *     but never offered);
 *   - apply the optional applicant-type / division scope (Req 5.1): a title with
 *     an `applicantType` (or `divisionKey`) set is selectable only by a matching
 *     contestant; an unscoped title is selectable by every contestant of the
 *     year (Req 1.6);
 *   - sort by `displayOrder` (Req 1.7).
 */

import { useEffect, useState } from 'react';

import { applicationFormClient } from '@/lib/application-form/client';
import type { Title } from '@/lib/application-form/client';
import { isSeasonTitleRegistryEnabled } from '@/config/featureFlags';

/** A selectable title option (the persisted value is the `label`). */
export interface TitleOption {
  key: string;
  label: string;
}

/** The state returned by {@link useApplicationTitles}. */
export interface UseApplicationTitlesResult {
  /** Active, in-scope titles for the year, sorted by displayOrder. */
  titles: Title[];
  /** The `{ key, label }` options the selection controls consume. */
  options: TitleOption[];
  loading: boolean;
  error: string | null;
}

/** Whether a catalog title is selectable by a contestant with the given scope. */
function titleInScope(
  title: Title,
  applicantType: string | undefined,
  divisionKey: string | undefined,
): boolean {
  // An applicant-type-scoped title is selectable only by a matching applicant
  // type; case-insensitive so a MISS/miss mismatch never hides an option.
  if (title.applicantType != null && title.applicantType.trim() !== '') {
    if (
      applicantType == null ||
      applicantType.trim().toLowerCase() !== title.applicantType.trim().toLowerCase()
    ) {
      return false;
    }
  }
  // A division-scoped title is selectable only by a matching division.
  if (title.divisionKey != null && title.divisionKey.trim() !== '') {
    if (divisionKey == null || divisionKey.trim() !== title.divisionKey.trim()) {
      return false;
    }
  }
  return true;
}

/**
 * Load the active, in-scope season Title_Catalog for `competitionYear`.
 *
 * @param competitionYear the applicant's current-season competition year.
 * @param applicantType optional applicant-type scope (e.g. `MISS`/`MRS`).
 * @param divisionKey optional division scope (the contestant's division key).
 */
export function useApplicationTitles(
  competitionYear: number,
  applicantType?: string,
  divisionKey?: string,
): UseApplicationTitlesResult {
  const [titles, setTitles] = useState<Title[]>([]);
  const [options, setOptions] = useState<TitleOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Season Title catalog gate (season-scoped-title-registry, task 14): when
    // the flag is OFF the catalog is inert — the hook offers NO options so the
    // contestant/director title controls fall back to free-text entry, exactly
    // as before the catalog shipped. This keeps the deploy inert until the
    // production backfill has seeded the catalog and the flag is flipped.
    if (!isSeasonTitleRegistryEnabled()) {
      setTitles([]);
      setOptions([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const all = await applicationFormClient.listTitles(competitionYear);
        if (cancelled) return;
        const list = (all ?? [])
          .filter((t) => t.active && titleInScope(t, applicantType, divisionKey))
          .slice()
          .sort((a, b) => a.displayOrder - b.displayOrder);
        setTitles(list);
        setOptions(list.map((t) => ({ key: t.key, label: t.label })));
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load titles.');
          setTitles([]);
          setOptions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [competitionYear, applicantType, divisionKey]);

  return { titles, options, loading, error };
}

export default useApplicationTitles;
